// ===================================
// TRENCH QUEST - Simulated Market Types
// Types for the simulated market system with AI-driven price movements
// ===================================

/**
 * Price history point
 */
export interface PricePoint {
  timestamp: number
  price: number
  gameHour: number
}

/**
 * Price effect from story/AI events
 */
export interface PriceEffect {
  id: string
  token: string
  targetChange: number      // % change target (e.g., 15 means +15%)
  duration: number          // Game hours to reach target
  startTime: number         // Game hour when effect started
  reason: string            // Narrative reason for the change
  easing: 'linear' | 'spike' | 'gradual'
  applied: number           // % of effect already applied
}

/**
 * Simulated token price with history and modifiers
 */
export interface SimulatedTokenPrice {
  symbol: string
  name: string
  baseRealPrice: number         // Price at game start (from Jupiter/CoinGecko)
  currentPrice: number          // Current simulated price
  priceHistory: PricePoint[]    // Rolling 24h history
  change24h: number             // 24h change percentage
  change1h: number              // 1h change percentage
  volatilityMultiplier: number  // AI can adjust (default 1.0)
  driftBias: number             // AI can set bearish (-1) to bullish (+1)
  pendingEffects: PriceEffect[] // Active price effects from story
  lastUpdateHour: number        // Last game hour when updated
  isRugged: boolean             // Token has been rugged
}

/**
 * Market event generated by simulation
 */
export interface SimulatedMarketEvent {
  id: string
  type: 'pump' | 'dump' | 'rug' | 'recovery' | 'whale_move' | 'news'
  token: string
  priceChange: number
  headline: string
  description: string
  gameHour: number
  timestamp: number
}

/**
 * Full simulated market state
 */
export interface SimulatedMarketState {
  // Token prices
  prices: Record<string, SimulatedTokenPrice>

  // Timing
  gameStartTime: number         // Real timestamp when game started
  currentGameHour: number       // Total game hours elapsed
  lastUpdateTime: number        // Real timestamp of last update
  lastUpdateGameHour: number    // Game hour of last update

  // Time configuration
  timeScale: number             // Real minutes per game hour (e.g., 1 = 1 min = 1 game hour)

  // Market conditions
  condition: 'bull' | 'bear' | 'crab'
  fearGreedIndex: number        // 0-100
  globalVolatility: number      // Multiplier for all token volatility

  // Rugged tokens
  ruggedTokens: string[]

  // Recent events for display
  recentEvents: SimulatedMarketEvent[]
}

/**
 * Configuration for initializing simulated market
 */
export interface SimulatedMarketConfig {
  difficulty: 'paper_hands' | 'diamond_hands' | 'gigabrain'
  timeScale: number
  initialCondition?: 'bull' | 'bear' | 'crab'
  volatilityMultiplier?: number
}

/**
 * Result of a market update tick
 */
export interface MarketUpdateResult {
  market: SimulatedMarketState
  events: SimulatedMarketEvent[]
  gameHoursElapsed: number
}

/**
 * AI market effect instruction (from story outcomes)
 */
export interface AIMarketEffect {
  token: string
  priceImpact: number           // % change (positive or negative)
  reason: string
  duration?: number             // Game hours (default: 1)
  easing?: 'linear' | 'spike' | 'gradual'
}

/**
 * Token category volatility multipliers
 */
export const CATEGORY_VOLATILITY: Record<string, number> = {
  stable: 0.001,
  blue_chip: 0.15,
  mid_cap: 0.30,
  degen: 0.50,
  ultra_degen: 0.75,
}

/**
 * Time scale presets
 */
export const TIME_SCALES = {
  relaxed: 2,     // 2 real minutes = 1 game hour (slower)
  normal: 1,      // 1 real minute = 1 game hour
  fast: 0.5,      // 30 real seconds = 1 game hour
  turbo: 0.25,    // 15 real seconds = 1 game hour
} as const

/**
 * Difficulty modifiers for market simulation
 */
export const DIFFICULTY_MODIFIERS: Record<string, {
  volatility: number
  rugChance: number
  recoveryChance: number
  timeScale: number
}> = {
  paper_hands: {
    volatility: 0.7,      // Lower volatility
    rugChance: 0.001,     // Very low rug chance
    recoveryChance: 0.8,  // High recovery chance
    timeScale: 2,         // Slower time
  },
  diamond_hands: {
    volatility: 1.0,      // Normal volatility
    rugChance: 0.005,     // Normal rug chance
    recoveryChance: 0.5,  // Normal recovery
    timeScale: 1,         // Normal time
  },
  gigabrain: {
    volatility: 1.5,      // Higher volatility
    rugChance: 0.01,      // Higher rug chance
    recoveryChance: 0.3,  // Lower recovery
    timeScale: 0.5,       // Faster time
  },
}
